import api from "@/services/api";
import type { QueryFunctionContext } from "@/utils/typing";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { reactive, computed, type ComputedRef } from "vue";
import { useAuthStore } from "@/store/auth";
import {
  PublicDashboardFilters,
  usePublicDashboardQueryFilters,
} from "@/services/public_dashboard";
import { setTag } from "@sentry/vue";
import { User, UserRole } from "@/services/user";
import { DateTime } from "luxon";

export enum Tier {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export interface SubscriptionLimits {
  websiteOptimizationPageLimit: number;
  competitorLimit: number;
}

export interface Organization {
  id: number;
  createdAt: DateTime;
  name: string;
  preferredCurrency: string;
  preferredTimezone: string;
  scrapingIgnoreQueryParams: boolean;
  scrapingUsePlaywright: boolean;
  scrapingFrequency: "WEEKLY" | "BIWEEKLY" | "MONTHLY";
  nextScrapeAt: string | null;
  subscriptionTier: Tier;
  subscriptionEnd: string;
  subscriptionLimits: SubscriptionLimits;
  domains: string[];
  alternativeNames: string[];
  manuallyFetchedSiteScoreAt: string | null;
  canFetchSiteScoreManually: boolean;
  isAgency: boolean;
  hasAgencyParent: boolean;
  iconUrl: string | null;
  userCount: number;
  pendingInviteCount: number;
  userSeatLimit: number;
  agencyHubEnabled: boolean;
  // Resolved nearest is_agency ancestor for non-agency descendants — null
  // when this org is itself an agency, has no parent, or its chain has no
  // agency in it. Frontend cannot reliably walk parentId on its own because
  // /auth/organizations/ only returns orgs the user has direct access to.
  ancestorAgency: {
    id: number;
    name: string;
    agencyHubEnabled: boolean;
  } | null;
  // Hierarchy fields. parentId is null for top-level orgs (agencies or
  // standalone). level is auto-computed from the parent chain (0 = root).
  parentId: number | null;
  level: number;
}

export interface AgencySubOrganizationsResponse {
  subOrganizationsWithParentAgencyUsers: Record<string, string[]>;
  subOrganizationDetails: Record<string, Organization>;
  userDetails: Record<string, User>;
}

export interface Currency {
  code: string;
  description: string;
}

export interface Timezone {
  name: string;
}

export const GET_ORGANIZATION_URL = "/auth/organization/";

function getOrganization({
  queryKey: [, params],
}: QueryFunctionContext<PublicDashboardFilters>): Promise<Organization> {
  return api.get(GET_ORGANIZATION_URL, { params }).then((response) => {
    const organization = response.data;
    organization.createdAt = DateTime.fromISO(organization.createdAt);
    setTag("organization.id", organization.id);
    setTag("organization.name", organization.name);
    return organization;
  });
}

export function useOrganizationQuery() {
  const authStore = useAuthStore();
  const publicDashboardQueryFilters = usePublicDashboardQueryFilters();
  return reactive(
    useQuery({
      queryKey: ["organization", { ...publicDashboardQueryFilters }] as const,
      queryFn: getOrganization,
      enabled: computed(
        () =>
          authStore.isAuthed ||
          publicDashboardQueryFilters.dashboard !== undefined,
      ),
    }),
  );
}

function listOrganizations(): Promise<Organization[]> {
  return api.get("/auth/organizations/").then((response) => response.data);
}

export function useOrganizationsQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["organizations"] as const,
      queryFn: listOrganizations,
      enabled: computed(() => authStore.isAuthed),
    }),
  );
}

/**
 * Resolve the nearest ancestor agency for the given org id.
 *
 * Returns `{ id, agencyHubEnabled }` for the agency that governs hub access:
 * - the org itself when it is an agency,
 * - its serializer-provided `ancestorAgency` payload otherwise,
 * - `undefined` when no agency is in the chain or inputs aren't loaded yet.
 *
 * The frontend can't walk `parentId` reliably on its own because
 * `/auth/organizations/` only returns orgs the user has direct access to —
 * a Branch user typically lacks the parent agency in their switcher list.
 * `ancestorAgency` is computed server-side and travels with each org payload.
 */
export interface AgencyAncestor {
  id: number;
  agencyHubEnabled: boolean;
}

export function resolveAgencyAncestor(
  orgId: number | null | undefined,
  organizations: readonly Organization[] | undefined,
): AgencyAncestor | undefined {
  if (orgId === null || orgId === undefined || !organizations) return undefined;
  const org = organizations.find((o) => o.id === orgId);
  if (!org) return undefined;
  if (org.isAgency) {
    return { id: org.id, agencyHubEnabled: org.agencyHubEnabled };
  }
  return org.ancestorAgency
    ? {
        id: org.ancestorAgency.id,
        agencyHubEnabled: org.ancestorAgency.agencyHubEnabled,
      }
    : undefined;
}

function listAgencySubOrganizations(): Promise<AgencySubOrganizationsResponse> {
  return api
    .get("/auth/agency_sub_organizations/")
    .then((response) => response.data);
}

export function useAgencySubOrganizationsQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["agencySubOrganizations"] as const,
      queryFn: listAgencySubOrganizations,
      enabled: computed(() => authStore.isAuthed),
    }),
  );
}

function updateOrganization(
  organization: Partial<
    Pick<
      Organization,
      | "name"
      | "preferredCurrency"
      | "preferredTimezone"
      | "domains"
      | "alternativeNames"
      | "scrapingFrequency"
    >
  >,
): Promise<Organization> {
  return api
    .patch("/auth/organization/", organization)
    .then((response) => response.data);
}

export function useUpdateOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateOrganization,
      onSuccess: (organization) => {
        queryClient.setQueryData<Organization>(
          ["organization", {}],
          organization,
        );
        queryClient.invalidateQueries({ queryKey: ["organization"] });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
        // Because these include organization names, we invalidate them in case the name was changed.
        queryClient.invalidateQueries({ queryKey: ["users"] });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["changelogEntries"] });
      },
    }),
  );
}

function listSupportedCurrencies(): Promise<Currency[]> {
  return api
    .get("/auth/supported_currencies/")
    .then((response) => response.data);
}

export function useSupportedCurrenciesQuery() {
  return reactive(
    useQuery({
      queryKey: ["supportedCurrencies"],
      queryFn: listSupportedCurrencies,
    }),
  );
}

function listSupportedTimezones(): Promise<Timezone[]> {
  return api
    .get("/auth/supported_timezones/")
    .then((response) => response.data);
}

export function useSupportedTimezonesQuery() {
  return reactive(
    useQuery({
      queryKey: ["supportedTimezones"],
      queryFn: listSupportedTimezones,
    }),
  );
}

export interface CreateOrganizationPayload {
  name: string;
  preferredCurrency: string;
  domains: string[];
  users: { email: string; role: UserRole }[];
  isDemo?: boolean;
  isAgency?: boolean;
}
function createOrganization(
  payload: CreateOrganizationPayload,
): Promise<Organization> {
  return api
    .post("/auth/organizations/", payload)
    .then((response) => response.data);
}

export function useCreateOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createOrganization,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
        queryClient.invalidateQueries({
          queryKey: ["agency_sub_organizations"],
        });
      },
    }),
  );
}

// Agency sub-organization management

export interface CreateAgencySubOrganizationPayload {
  name: string;
  preferredCurrency: string;
  // Defaults server-side to the requesting agency. Set to nest the new org
  // somewhere deeper in the agency's subtree (e.g. under a Konsern or Branch).
  parentId?: number;
  domains?: string[];
  users?: { userId: number; role: UserRole }[];
  invites?: { email: string; role: UserRole }[];
}

function createAgencySubOrganization(
  payload: CreateAgencySubOrganizationPayload,
): Promise<Organization> {
  return api
    .post("/auth/agency_sub_organizations/create/", payload)
    .then((response) => response.data);
}

export function useCreateAgencySubOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createAgencySubOrganization,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencySubOrganizations"] });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
        // Refresh the hub-backed parent picker so the newly-created org
        // shows up immediately if the user picks "Create another".
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
      },
    }),
  );
}

function listAgencyUsers(): Promise<User[]> {
  return api.get("/auth/agency_users/").then((response) => response.data);
}

export function useAgencyUsersQuery(options?: {
  enabled?: ComputedRef<boolean>;
}) {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["agencyUsers"] as const,
      queryFn: listAgencyUsers,
      enabled: computed(
        () => authStore.isAuthed && (options?.enabled?.value ?? true),
      ),
    }),
  );
}

// Scopes a Hub action to a specific agency root. Optional: Hub callers pass the
// card's agencyRootId so multi-agency actions hit the right agency; non-Hub
// callers (org creation/settings) omit it and the backend falls back to the
// user's selection/home agency.
function agencyRootQuery(agencyRootId?: number): string {
  return agencyRootId === undefined ? "" : `?agency_root_id=${agencyRootId}`;
}

interface AddUserToSubOrgPayload {
  organizationId: number;
  agencyRootId?: number;
  userId: number;
  role: UserRole;
}

function addUserToSubOrganization(
  payload: AddUserToSubOrgPayload,
): Promise<void> {
  return api.post(
    `/auth/agency_sub_organizations/${payload.organizationId}/users/${payload.userId}/add/${agencyRootQuery(payload.agencyRootId)}`,
    { role: payload.role },
  );
}

export function useAddUserToSubOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: addUserToSubOrganization,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencySubOrganizations"] });
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
      },
    }),
  );
}

interface UpdateUserRolePayload {
  organizationId: number;
  agencyRootId?: number;
  userId: number;
  role: UserRole;
}

function updateSubOrganizationUserRole(
  payload: UpdateUserRolePayload,
): Promise<void> {
  return api.patch(
    `/auth/agency_sub_organizations/${payload.organizationId}/users/${payload.userId}/update_role/${agencyRootQuery(payload.agencyRootId)}`,
    { role: payload.role },
  );
}

export function useUpdateSubOrganizationUserRoleMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateSubOrganizationUserRole,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencySubOrganizations"] });
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
      },
    }),
  );
}

interface RemoveUserFromSubOrgPayload {
  organizationId: number;
  agencyRootId?: number;
  userId: number;
}

function removeUserFromSubOrganization(
  payload: RemoveUserFromSubOrgPayload,
): Promise<void> {
  return api.delete(
    `/auth/agency_sub_organizations/${payload.organizationId}/users/${payload.userId}/remove/${agencyRootQuery(payload.agencyRootId)}`,
  );
}

export function useRemoveUserFromSubOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: removeUserFromSubOrganization,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencySubOrganizations"] });
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
      },
    }),
  );
}

function joinSubOrganization(payload: {
  organizationId: number;
  agencyRootId?: number;
}): Promise<void> {
  return api.post(
    `/auth/agency_sub_organizations/${payload.organizationId}/join/${agencyRootQuery(payload.agencyRootId)}`,
  );
}

export function useJoinSubOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: joinSubOrganization,
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["agencySubOrganizations"],
          }),
          queryClient.invalidateQueries({ queryKey: ["agencyHub"] }),
          queryClient.invalidateQueries({ queryKey: ["organizations"] }),
        ]);
      },
    }),
  );
}

// Agency Hub

export interface HubOrganizationUser {
  id: number;
  name: string;
  email: string;
  isCurrentUser: boolean;
  role: string;
  profilePictureUrl?: string | null;
}

export interface ProblematicResource {
  id: number;
  name: string;
  channel: string;
  issueType: "inaccessible" | "expiring" | "expired" | "revoked";
  expiresAt: string | null;
}

export interface HubOrganization {
  id: number;
  name: string;
  icon: string | null;
  isActive: boolean;
  hasAccess: boolean;
  isFavorite: boolean;
  preferredCurrency: string;
  preferredTimezone: string;
  resourceHealth: {
    total: number;
    healthy: number;
    problematic: ProblematicResource[];
  };
  agencyUsers: HubOrganizationUser[];
  goals: import("@/services/goal").Goal[];
  kpis: import("@/services/kpi").KPI[];
  primaryUserCount: number;
  // Hierarchy fields. parentId is the actual parent in the chain — null when
  // the org is a direct child of the agency root. level is auto-computed.
  // path lists names from the topmost descendant down to this org (excludes
  // the agency root) so the UI can render breadcrumbs without re-walking.
  parentId: number | null;
  level: number;
  path: string[];
  // Which member agency (hub root) this sub-org belongs to. Matches an entry
  // in AgencyHubData.agencies — used to group cards when the user belongs to
  // several agencies.
  agencyRootId: number;
}

export interface HubAgency {
  id: number;
  name: string;
  isAgency: boolean;
}

export interface HubAgencyWithRole extends HubAgency {
  // The requesting user's role in this specific agency (MANAGER/USER). Lets
  // the UI reason about per-agency permissions when the hub spans several.
  currentUserRole: string;
}

export interface AgencyHubData {
  // The user's home agency; kept for backward compatibility. Prefer `agencies`.
  agency: HubAgency;
  // Every agency the user is a member of, each a top-level hub root with the
  // user's role in it. One entry for a single-agency user; several for a user
  // in sibling agencies.
  agencies: HubAgencyWithRole[];
  subOrganizations: HubOrganization[];
  // The user's role in their home agency. Per-agency roles are on `agencies`.
  currentUserRole: string;
}

function getAgencyHub(): Promise<AgencyHubData> {
  return api.get("/auth/hub/").then((response) => response.data);
}

export function useAgencyHubQuery(options?: {
  enabled?: ComputedRef<boolean>;
}) {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["agencyHub"] as const,
      queryFn: getAgencyHub,
      enabled: computed(
        () => authStore.isAuthed && (options?.enabled?.value ?? true),
      ),
    }),
  );
}

interface UpdateSubOrganizationPayload {
  organizationId: number;
  agencyRootId?: number;
  data: Partial<
    Pick<
      Organization,
      "name" | "preferredCurrency" | "preferredTimezone" | "iconUrl"
    >
  >;
}

function updateSubOrganization(
  payload: UpdateSubOrganizationPayload,
): Promise<Organization> {
  return api
    .patch(
      `/auth/agency_sub_organizations/${payload.organizationId}/update/${agencyRootQuery(payload.agencyRootId)}`,
      payload.data,
    )
    .then((response) => response.data);
}

export function useUpdateSubOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateSubOrganization,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
        queryClient.invalidateQueries({ queryKey: ["agencySubOrganizations"] });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
      },
    }),
  );
}

interface UploadIconResponse {
  url: string;
  key: string;
}

function uploadSubOrganizationIcon(
  organizationId: number,
  file: File,
  agencyRootId?: number,
): Promise<UploadIconResponse> {
  const formData = new FormData();
  formData.append("file", file);

  return api
    .post(
      `/auth/agency_sub_organizations/${organizationId}/upload_icon/${agencyRootQuery(agencyRootId)}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )
    .then((response) => response.data);
}

export function useUploadSubOrganizationIconMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: ({
        organizationId,
        agencyRootId,
        file,
      }: {
        organizationId: number;
        agencyRootId?: number;
        file: File;
      }) => uploadSubOrganizationIcon(organizationId, file, agencyRootId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
        queryClient.invalidateQueries({ queryKey: ["agencySubOrganizations"] });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
      },
    }),
  );
}

// Organization icon upload

function uploadOrganizationIcon(file: File): Promise<UploadIconResponse> {
  const formData = new FormData();
  formData.append("file", file);

  return api
    .post("/auth/organization/upload_icon/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => response.data);
}

export function useUploadOrganizationIconMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: (file: File) => uploadOrganizationIcon(file),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["organization"] });
      },
    }),
  );
}

// Favorites

function addFavoriteOrganization(payload: {
  organizationId: number;
  agencyRootId?: number;
}): Promise<void> {
  return api.post(
    `/auth/favorites/${payload.organizationId}/add/${agencyRootQuery(payload.agencyRootId)}`,
  );
}

export function useAddFavoriteOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: addFavoriteOrganization,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
      },
    }),
  );
}

function removeFavoriteOrganization(payload: {
  organizationId: number;
  agencyRootId?: number;
}): Promise<void> {
  return api.delete(
    `/auth/favorites/${payload.organizationId}/remove/${agencyRootQuery(payload.agencyRootId)}`,
  );
}

export function useRemoveFavoriteOrganizationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: removeFavoriteOrganization,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["agencyHub"] });
      },
    }),
  );
}
